export type PassTypeId = 'el_abra' | 'enaex' | 'dmh' | 'dgm';

export type PassFieldCategory = 'PASE' | 'LICENCIA';

export type PassFieldDefinition = {
  key: string;
  label: string;
  category: PassFieldCategory;
  placeholder?: string;
  colSpan?: number;
};

export type PassTypeDefinition = {
  id: PassTypeId;
  label: string;
  description: string;
  matchPatterns: string[];
  fields: PassFieldDefinition[];
};

export type PassExtensionRecord = {
  id?: number;
  name: string;
  date_expired: string | null;
  tipo: PassFieldCategory;
};

export type PassTypeState = {
  enabled: boolean;
  values: Record<string, string>;
};

const elAbraFields: PassFieldDefinition[] = [
  { key: 'contrato', label: 'Contrato', category: 'PASE' },
  { key: 'anexo', label: 'Anexo', category: 'PASE' },
  { key: 'cedula_identidad', label: 'Cédula identidad', category: 'PASE' },
  { key: 'examen_ocupacional', label: 'Examen ocupacional', category: 'PASE' },
  { key: 'certificado_antecedentes', label: 'Certificado de antecedentes', category: 'PASE' },
  { key: 'irl', label: 'IRL', category: 'PASE' },
  { key: 'foto_conductor', label: 'Foto del conductor', category: 'PASE' },
  { key: 'reglamento_interno', label: 'Reglamento interno', category: 'PASE' },
  { key: 'lototo', label: 'LOTOTO', category: 'PASE' },
  { key: 'curso_riesgos_laborales', label: 'Curso riesgos laborales', category: 'PASE' },
  { key: 'licencia_conducir', label: 'Licencia conducir', category: 'LICENCIA' },
  { key: 'examen_sensometrico', label: 'Examen sensométrico', category: 'LICENCIA' },
  { key: 'hoja_vida_conductor', label: 'Hoja de vida conductor', category: 'LICENCIA' },
  { key: 'induccion_conduccion', label: 'Inducción de conducción', category: 'LICENCIA' },
  { key: 'certificado_competencias', label: 'Certificado de competencias', category: 'LICENCIA' },
];

const dmhFields: PassFieldDefinition[] = [
  { key: 'cedula_identidad', label: 'Cédula identidad', category: 'PASE' },
  { key: 'foto', label: 'Foto', category: 'PASE' },
  { key: 'contrato_trabajo', label: 'Contrato de trabajo', category: 'PASE' },
  { key: 'irl', label: 'IRL', category: 'PASE' },
  { key: 'examen_ocupacional', label: 'Examen ocupacional', category: 'PASE' },
  { key: 'curso_hombre_nuevo', label: 'Curso hombre nuevo', category: 'PASE' },
  { key: 'reglamento_interno', label: 'Reglamento interno', category: 'PASE' },
  { key: 'anexo_contrato', label: 'Anexo de contrato', category: 'PASE' },
  { key: 'anexo_exclusividad', label: 'Anexo de exclusividad', category: 'PASE' },
  { key: 'declaracion_individual_trabajador', label: 'Declaración individual de trabajador', category: 'PASE' },
  { key: 'licencia_conducir', label: 'Licencia de conducir', category: 'LICENCIA' },
  { key: 'examen_sensometrico', label: 'Examen sensométrico', category: 'LICENCIA' },
  { key: 'curso_manejo_defensiva', label: 'Curso de manejo a la defensiva', category: 'LICENCIA' },
  { key: 'hoja_vida_conductor', label: 'Hoja vida conductor', category: 'LICENCIA' },
  { key: 'induccion_area', label: 'Inducción del área', category: 'LICENCIA' },
  { key: 'certificado_competencia', label: 'Certificado de competencia', category: 'LICENCIA' },
  { key: 'examen_riesgo_conduccion', label: 'Examen con riesgo específico a la conducción', category: 'LICENCIA', colSpan: 2 },
];

const dgmFields: PassFieldDefinition[] = [
  { key: 'cedula_identidad', label: 'Cédula de identidad', category: 'PASE' },
  { key: 'foto_conductor', label: 'Foto conductor', category: 'PASE' },
  { key: 'contrato_trabajo', label: 'Contrato de trabajo', category: 'PASE' },
  { key: 'curso_hombre_nuevo', label: 'Curso hombre nuevo', category: 'PASE' },
  { key: 'examen_ocupacional', label: 'Examen ocupacional', category: 'PASE' },
  { key: 'irl', label: 'IRL', category: 'PASE' },
  { key: 'anexo_contrato', label: 'Anexo contrato', category: 'PASE' },
  { key: 'anexo_exclusividad', label: 'Anexo de exclusividad', category: 'PASE' },
  { key: 'declaracion_individual_trabajador', label: 'Declaración individual del trabajador', category: 'PASE' },
  { key: 'licencia_conducir', label: 'Licencia de conducir', category: 'LICENCIA' },
  { key: 'examen_sensometrico', label: 'Examen sensométrico', category: 'LICENCIA' },
  { key: 'manejo_defensiva', label: 'Manejo a la defensiva', category: 'LICENCIA' },
  { key: 'examen_riesgo_conduccion', label: 'Examen con riesgo específico a la conducción', category: 'LICENCIA', colSpan: 2 },
  { key: 'hoja_vida_conductor', label: 'Hoja de vida de conductor', category: 'LICENCIA' },
  { key: 'induccion_conduccion', label: 'Inducción de conducción', category: 'LICENCIA' },
  { key: 'certificado_competencia', label: 'Certificado de competencia', category: 'LICENCIA' },
];

export const PASS_TYPE_DEFINITIONS: PassTypeDefinition[] = [
  {
    id: 'el_abra',
    label: 'Pase El Abra',
    description: 'Credenciales requeridas para operar en faena El Abra.',
    matchPatterns: ['el abra', 'elabra'],
    fields: elAbraFields,
  },
  {
    id: 'enaex',
    label: 'Pase Enaex',
    description: 'Documentación de ingreso y vencimientos para faena Enaex.',
    matchPatterns: ['enaex'],
    fields: [
      { key: 'licencia', label: 'Licencia de conducir', category: 'LICENCIA' },
      { key: 'induccion', label: 'Inducción', category: 'PASE' },
      { key: 'examen_medico', label: 'Examen pre-ocupacional', category: 'PASE' },
    ],
  },
  {
    id: 'dmh',
    label: 'Pase DMH',
    description: 'Credenciales y certificaciones para acceso DMH.',
    matchPatterns: ['dmh'],
    fields: dmhFields,
  },
  {
    id: 'dgm',
    label: 'Pase DGM',
    description: 'Registro y certificaciones exigidas por la DGM.',
    matchPatterns: ['dgm'],
    fields: dgmFields,
  },
];

export const DRIVER_PASS_TYPES: PassTypeId[] = ['el_abra', 'dmh', 'dgm'];

export const DRIVER_PASS_TABS = PASS_TYPE_DEFINITIONS.filter((def) =>
  DRIVER_PASS_TYPES.includes(def.id)
);

export type DriverPassTabId = 'el_abra' | 'dmh' | 'dgm';

export function getFieldsByCategory(
  definition: PassTypeDefinition,
  category: PassFieldCategory
): PassFieldDefinition[] {
  return definition.fields.filter((field) => field.category === category);
}

export function normalizePassName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function resolvePassTypeFromName(name: string): PassTypeDefinition | null {
  const normalized = normalizePassName(name);
  if (!normalized) return null;

  return (
    PASS_TYPE_DEFINITIONS.find((def) =>
      def.matchPatterns.some((pattern) => normalized.includes(pattern))
    ) ?? null
  );
}

export function buildEmptyFieldValues(typeId: PassTypeId): Record<string, string> {
  const definition = PASS_TYPE_DEFINITIONS.find((t) => t.id === typeId);
  if (!definition) return {};
  return definition.fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = '';
    return acc;
  }, {});
}

export function getPassTypeById(id: PassTypeId): PassTypeDefinition {
  return PASS_TYPE_DEFINITIONS.find((t) => t.id === id)!;
}

export function getPassTabById(id: DriverPassTabId): PassTypeDefinition {
  return getPassTypeById(id);
}

export function buildInitialPassState(): Record<DriverPassTabId, PassTypeState> {
  return DRIVER_PASS_TYPES.reduce(
    (acc, passType) => {
      acc[passType as DriverPassTabId] = {
        enabled: passType === 'el_abra',
        values: buildEmptyFieldValues(passType),
      };
      return acc;
    },
    {} as Record<DriverPassTabId, PassTypeState>
  );
}

export function extensionsToValues(
  extensions: PassExtensionRecord[] | undefined
): Record<string, string> {
  const values: Record<string, string> = {};
  (extensions ?? []).forEach((item) => {
    values[item.name] = item.date_expired ?? '';
  });
  return values;
}

export function buildExtensionsPayload(
  definition: PassTypeDefinition,
  values: Record<string, string>
): PassExtensionRecord[] {
  return definition.fields
    .filter((field) => values[field.key]?.trim())
    .map((field) => ({
      name: field.key,
      date_expired: values[field.key].trim(),
      tipo: field.category,
    }));
}

export function mergePassExtensionsFromApi(
  apiData: Record<string, { enabled?: boolean; extensions?: PassExtensionRecord[] }> | undefined
): Record<DriverPassTabId, PassTypeState> {
  const initial = buildInitialPassState();

  DRIVER_PASS_TYPES.forEach((passType) => {
    const remote = apiData?.[passType];
    if (!remote) return;

    initial[passType as DriverPassTabId] = {
      enabled: Boolean(remote.enabled),
      values: {
        ...initial[passType as DriverPassTabId].values,
        ...extensionsToValues(remote.extensions),
      },
    };
  });

  return initial;
}

/** @deprecated Usar buildEmptyFieldValues */
export function buildMockFieldValues(typeId: PassTypeId): Record<string, string> {
  return buildEmptyFieldValues(typeId);
}

export type PassTabId = DriverPassTabId;
